import React from 'react';
import moment from 'moment';
import useValidator from '../../../hooks/useValidator';
import { AuthUser, Sex } from '../../../types/types';
import Input from '../../Inputs/Input/Input';
import { formatPhoneNumber, GENDERS, US_STATES } from '../../../utils/utils';
import SelectInput from '../../Inputs/SelectInput/SelectInput';
import Button from '../../Button/Button';

type Account = {
  firstName: string;
  lastName: string;
  email: string;
  gender: string;
  phoneNumber: string;
  zipCode: string;
  state: string;
};

type AccountFormProps = {
  account: Account;
  isUpdating: boolean;
  onUpdateAccount: (payload: Partial<AuthUser>) => void;
};

const customValidators = {
  date: {
    message: 'The date of birth must be a date (mm/dd/yyyy).',
    rule: (val: string) => {
      return moment(val, 'MM/DD/YYYY', true).isValid();
    },
    required: false,
  },
};

function AccountDetailsForm({ account, onUpdateAccount, isUpdating }: AccountFormProps) {
  const [validator, showValidationMessage] = useValidator({}, customValidators);
  const [accountDetails, setAccountDetails] = React.useState<Account>(account);

  const handleAccountInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;

    setAccountDetails(acc => ({
      ...acc,
      [name]: value,
    }));
  };

  const handleAccountUpdate = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    const payload = {
      firstName: accountDetails.firstName,
      lastName: accountDetails.lastName,
      email: accountDetails.email,
      state: accountDetails.state,
      zipCode: accountDetails.zipCode,
      patientDetails: {
        gender: accountDetails.gender as Sex,
      },
    };

    onUpdateAccount(payload);
  };

  return (
    <div className="h-full relative">
      <div className="grid tablet:grid-cols-2 gap-6 w-full mx-auto">
        <div>
          <Input
            name="firstName"
            placeholder="First name"
            type="text"
            value={accountDetails.firstName}
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/firstName:!border-river-red 
                  peer-[.validator-error]/firstName:!mb-5 peer-[.validator-error]/firstName:focus:!ring-river-red"
            validator={validator.message(
              'firstName',
              accountDetails.firstName,
              'required|alpha_space|min:2',
              {
                messages: {
                  required: 'First name is required.',
                },
                className: 'peer/firstName validator-error text-left mt-2 top-[40px] ',
              },
            )}
          />
        </div>
        <div>
          <Input
            name="lastName"
            placeholder="Last name"
            type="text"
            value={accountDetails.lastName}
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/lastName:!border-river-red 
                  peer-[.validator-error]/lastName:!mb-5 peer-[.validator-error]/lastName:focus:!ring-river-red"
            validator={validator.message(
              'lastName',
              accountDetails.lastName,
              'required|alpha_space|min:2',
              {
                messages: {
                  required: 'Last name is required.',
                },
                className: 'peer/lastName validator-error text-left mt-2 top-[40px] ',
              },
            )}
          />
        </div>
        <div className="tablet:col-span-2 laptop:tablet:col-span-1">
          <Input
            name="email"
            placeholder="Email"
            value={accountDetails.email}
            type="email"
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/email:!border-river-red 
                  peer-[.validator-error]/email:!mb-5 peer-[.validator-error]/email:focus:!ring-river-red"
            validator={validator.message('email', accountDetails.email, 'required|email', {
              messages: {
                required: 'Email address is required.',
              },
              className: 'peer/email validator-error text-left mt-2 top-[40px] ',
            })}
          />
        </div>
        <div className="tablet:col-span-2 laptop:tablet:col-span-1">
          <Input
            name="phoneNumber"
            placeholder="Phone number"
            type="text"
            value={formatPhoneNumber(accountDetails.phoneNumber)}
            disabled
            onChange={() => false}
            onBlur={() => false}
            error={false}
          />
        </div>
        <div className="tablet:col-span-2 laptop:tablet:col-span-1">
          <SelectInput
            placeholder={{
              value: '',
              label: 'Gender',
            }}
            value={accountDetails.gender}
            options={GENDERS}
            name="gender"
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/gender:!border-river-red 
                  peer-[.validator-error]/gender:!mb-5 peer-[.validator-error]/gender:focus:!ring-river-red"
            validator={validator.message('gender', accountDetails.gender, 'required|string|min:3', {
              messages: {
                required: 'Gender is required.',
              },
              className: 'peer/gender validator-error text-left mt-2 top-[40px] ',
            })}
          />
        </div>
        <div className="tablet:col-span-2 laptop:tablet:col-span-1">
          <Input
            name="zipCode"
            placeholder="Zip code"
            type="text"
            value={accountDetails.zipCode}
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/zipCode:!border-river-red 
                  peer-[.validator-error]/zipCode:!mb-5 peer-[.validator-error]/zipCode:focus:!ring-river-red"
            validator={validator.message(
              'zipCode',
              accountDetails.zipCode,
              'required|numeric|min:3|max:5',
              {
                messages: {
                  required: 'Zip code is required.',
                },
                className: 'peer/zipCode validator-error text-left mt-2 top-[40px] ',
              },
            )}
          />
        </div>
        <div className="tablet:col-span-2 laptop:tablet:col-span-1">
          <SelectInput
            placeholder={{
              value: '',
              label: 'State',
            }}
            value={accountDetails.state}
            options={US_STATES}
            name="state"
            onChange={handleAccountInputChange}
            className="peer-[.validator-error]/state:!border-river-red 
                  peer-[.validator-error]/state:!mb-5 peer-[.validator-error]/state:focus:!ring-river-red"
            validator={validator.message('state', accountDetails.state, 'required|string', {
              messages: {
                required: 'State is required.',
              },
              className: 'peer/state validator-error text-left mt-2 top-[40px] ',
            })}
          />
        </div>
      </div>

      <div className="text-center w-full mt-[10%] relative tablet:text-left">
        <Button
          label="Update"
          onClick={handleAccountUpdate}
          className="w-[250px]"
          disabled={isUpdating}
          loading={isUpdating}
        />
      </div>
    </div>
  );
}

export default AccountDetailsForm;
